/**
 * Structured API error that carries the HTTP status code, when the API sends one its machine-readable
 * code, and the server's own message. Both the browser and the server fetch throw it, so callers
 * branch on `statusCode` / `code`, never on the message text.
 */
export class ApiError extends Error {
  statusCode: number;
  /** The API's machine-readable error code, when it sends one. */
  code?: string;
  /** The server's message as it sent it (`message` may add the call's context for the logs). */
  serverMessage?: string;
  constructor(message: string, statusCode: number, code?: string, serverMessage?: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.serverMessage = serverMessage;
  }
}

/** A TanStack Query `retry` that answers a 4xx at once (it is an answer, not a glitch) and retries anything else up to three times. */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  return !(error instanceof ApiError && error.statusCode < 500) && failureCount < 3;
}

/** The call's answer, or null when the API answers 404 (missing, or not visible to the caller); other failures throw. */
export async function nullOn404<T>(call: Promise<T>): Promise<T | null> {
  try {
    return await call;
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) return null;
    throw error;
  }
}
